import {
  QueryClient,
  VueQueryPlugin,
  type UseQueryOptions,
  type UseMutationOptions,
  type UseInfiniteQueryOptions,
  type QueryClientConfig,
  type QueryKey,
  type MutationKey
} from '@tanstack/vue-query'
import type { App } from 'vue'

/**
 * HTTP 请求方法类型
 */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/**
 * Query 配置选项
 */
export interface QueryOptions<TData = any, TError = Error> extends Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'> {
  queryKey?: QueryKey
}

/**
 * Mutation 配置选项
 */
export interface MutationOptions<TData = any, TError = Error, TVariables = any> extends Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'> {
  mutationKey?: MutationKey
}

/**
 * 无限查询配置选项
 */
export interface InfiniteQueryOptions<TData = any, TError = Error> extends Omit<UseInfiniteQueryOptions<TData, TError>, 'queryKey' | 'queryFn'> {
  queryKey?: QueryKey
}

/**
 * 分页参数接口
 */
export interface PaginationParams {
  page?: number
  pageSize?: number
  offset?: number
  limit?: number
}

/**
 * 分页响应接口
 */
export interface PaginationResponse<T = any> {
  data: T[]
  total: number
  page: number
  pageSize: number
  hasMore?: boolean
  nextCursor?: string | number
}

/**
 * 创建 QueryClient 配置
 */
export const createQueryClientConfig = (): QueryClientConfig => ({
  defaultOptions: {
    queries: {
      // 默认缓存时间 5 分钟
      staleTime: 5 * 60 * 1000,
      // 垃圾回收时间 10 分钟
      gcTime: 10 * 60 * 1000,
      // 重试配置
      retry: (failureCount, error: any) => {
        // 4xx 错误不重试
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false
        }
        // 最多重试 3 次
        return failureCount < 3
      },
      // 重试延迟
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      // 网络重连时重新获取
      refetchOnReconnect: true,
      // 窗口聚焦时重新获取
      refetchOnWindowFocus: false
    },
    mutations: {
      // 默认重试 1 次
      retry: 1,
      // 重试延迟
      retryDelay: 1000
    }
  }
})

/**
 * QueryClient 实例
 */
export const queryClient = new QueryClient(createQueryClientConfig())

/**
 * 安装 Vue Query 插件
 */
export function setupVueQuery(app: App) {
  app.use(VueQueryPlugin, {
    queryClient,
    // 开发环境下启用 devtools
    enableDevtoolsV6Plugin: import.meta.env.DEV
  })

  console.log('[CT_QUERY_CLIENT] TanStack Query 客户端初始化完成')
}

/**
 * 查询客户端工具函数
 */
export const queryClientUtils = {
  /**
   * 获取缓存统计信息
   */
  getCacheStats() {
    const queryCache = queryClient.getQueryCache()
    const mutationCache = queryClient.getMutationCache()

    return {
      queries: {
        total: queryCache.getAll().length,
        active: queryCache.getAll().filter(q => q.getObserversCount() > 0).length,
        stale: queryCache.getAll().filter(q => q.isStale()).length,
        fetching: queryCache.getAll().filter(q => q.state.fetchStatus === 'fetching').length
      },
      mutations: {
        total: mutationCache.getAll().length,
        pending: mutationCache.getAll().filter(m => m.state.status === 'pending').length
      }
    }
  },

  /**
   * 清理过期缓存
   */
  cleanupStaleQueries() {
    const queryCache = queryClient.getQueryCache()
    const staleQueries = queryCache.getAll().filter(q => q.isStale() && q.getObserversCount() === 0)

    staleQueries.forEach(query => {
      queryCache.remove(query)
    })

    console.log(`[CT_QUERY_CLEANUP] 清理了 ${staleQueries.length} 个过期查询`)
    return staleQueries.length
  },

  /**
   * 打印缓存状态（调试用）
   */
  debugCacheStatus() {
    const stats = this.getCacheStats()
    console.table(stats)

    // 打印所有查询的详细信息
    const queries = queryClient.getQueryCache().getAll()
    console.log('[CT_QUERY_DEBUG] 查询详情:', queries.map(q => ({
      queryKey: q.queryKey,
      state: q.state.status,
      dataUpdatedAt: new Date(q.state.dataUpdatedAt).toLocaleString(),
      observers: q.getObserversCount(),
      isStale: q.isStale(),
      isFetching: q.state.fetchStatus === 'fetching'
    })))
  }
}

// 开发环境下的调试功能
if (import.meta.env.DEV) {
  // 将工具函数挂载到全局对象上，方便调试
  (window as any).__queryClientUtils = queryClientUtils

  console.log('[CT_QUERY_DEBUG] 开发环境下可使用 window.__queryClientUtils 进行调试')
}
